// Number rendering for the /alkanes terminal.
//
// Two problems live here, both from the "numbers that lie or can't be compared"
// finding:
//
//  1. Prices in these pools span roughly twelve decades. A price carried at the
//     tokens' own 8dp divisibility FLOORS TO ZERO below 1e-8, so pools holding
//     real TVL rendered `0 frBTC / $0` as their price. Zero is not a small
//     number, it is a false statement. Prices are therefore carried at
//     PRICE_DECIMALS and rendered with significant digits, switching to the
//     subscript-zero convention (0.0₈12) once the leading-zero run gets too
//     long to count by eye.
//
//  2. The same class of quantity was rendered with and without thousands
//     separators 200px apart (`5847.10822873` beside `5,788.63714644`).
//     Anything not being actively typed into groups its digits.

/// Fixed-point resolution for prices. Reserves are 8dp, so their RATIO needs
/// considerably more than 8dp to survive; 18 covers every live pair with room.
const PRICE_DECIMALS = 18
export const PRICE_SCALE = 10n ** BigInt(PRICE_DECIMALS)

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉'
/// Leading-zero run at which a plain decimal stops being countable at a glance.
const SUBSCRIPT_AT = 4
/// Significant digits kept for a sub-1 price.
const SIG_DIGITS = 4
/// Decimals shown for a price of 1 or more — enough for frBTC pairs, capped
/// once the integer part carries the magnitude on its own.
const WHOLE_DP = 6
const WHOLE_DP_LARGE = 2

function subscript(count: number): string {
	return String(count)
		.split('')
		.map((d) => SUBSCRIPT_DIGITS[Number(d)])
		.join('')
}

/// `0.` + `zeros` zeros + `digits`, in whichever of the two notations reads.
function renderSubUnit(zeros: number, digits: string): string {
	if (zeros < SUBSCRIPT_AT) {
		return `0.${'0'.repeat(zeros)}${digits}`
	}
	return `0.0${subscript(zeros)}${digits}`
}

/// Split a fractional digit string (one char per decimal place, no `0.`) into
/// its leading-zero run and the significant digits that follow it.
function splitFraction(fraction: string, sig: number): { zeros: number; digits: string } {
	const zeros = fraction.length - fraction.replace(/^0+/, '').length
	const digits = fraction.slice(zeros, zeros + sig).replace(/0+$/, '')
	return { zeros, digits: digits || '0' }
}

/// Same split for a JS number in (0, 1). Exponential notation gives the
/// leading-zero run directly and stays exact for any magnitude a double can
/// hold, so there is no tail of prices too small to render.
function splitSmallNumber(value: number, sig: number): { zeros: number; digits: string } {
	const [mantissa, exponent] = value.toExponential(Math.max(0, sig - 1)).split('e')
	return {
		zeros: -Number(exponent) - 1,
		digits: mantissa.replace('.', '').replace(/0+$/, '') || '0'
	}
}

/// A fixed-point price (see PRICE_SCALE) as a display string. Sub-unit prices
/// keep their significant digits instead of truncating to `0`.
export function formatPrice(value: bigint, decimals: number = PRICE_DECIMALS): string {
	if (value < 0n) {
		return `-${formatPrice(-value, decimals)}`
	}
	if (value === 0n) {
		return '0'
	}
	const unit = 10n ** BigInt(decimals)
	if (value >= unit) {
		const whole = value / unit
		const dp = whole >= 1_000n ? WHOLE_DP_LARGE : WHOLE_DP
		const frac = (value % unit)
			.toString()
			.padStart(decimals, '0')
			.slice(0, dp)
			.replace(/0+$/, '')
		return `${whole.toLocaleString('en-US')}${frac ? `.${frac}` : ''}`
	}
	const { zeros, digits } = splitFraction(value.toString().padStart(decimals, '0'), SIG_DIGITS)
	return renderSubUnit(zeros, digits)
}

/// Dollars for a PRICE. Fixed 2dp from a cent up so a column of them lines up;
/// below a cent it follows `formatPrice`, because "$0.00" on a tradable market
/// reads as free.
///
/// Distinct from `formatUsd` in ./review, which prices a trade the user is
/// about to sign and is deliberately pinned to 2dp with a `<$0.01` floor.
export function formatUsdPrice(value: number): string {
	if (!Number.isFinite(value)) {
		return '—'
	}
	const sign = value < 0 ? '-' : ''
	const abs = Math.abs(value)
	if (abs >= 0.01) {
		return `${sign}$${abs.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		})}`
	}
	if (abs === 0) {
		return '$0.00'
	}
	const { zeros, digits } = splitSmallNumber(abs, SIG_DIGITS)
	return `${sign}$${renderSubUnit(zeros, digits)}`
}

/// Compact dollars for dense rows: $1.2M / $45.3K / $982.11. Price cells use
/// `formatUsdPrice` (sub-cent notation); this is for liquidity/volume/holdings,
/// where magnitude is the message.
///
/// Lived in the old asset-picker modal, which the discovery feed replaced. It
/// is a NUMBER RENDERER, so it belongs beside the other two rather than inside
/// whichever component happened to need it first.
export function usdCompact(n: number | null | undefined): string {
	if (n === null || n === undefined || !Number.isFinite(n)) {
		return '—'
	}
	const a = Math.abs(n)
	// Meme-token supplies push supply×price into the trillions and beyond;
	// without these tiers the row printed `$1288134811914.32B` — a real
	// number rendered unreadably, which is its own kind of dishonesty.
	if (a >= 1e12) {
		return `$${(n / 1e12).toFixed(2)}T`
	}
	if (a >= 1e9) {
		return `$${(n / 1e9).toFixed(2)}B`
	}
	if (a >= 1e6) {
		return `$${(n / 1e6).toFixed(2)}M`
	}
	if (a >= 1e3) {
		return `$${(n / 1e3).toFixed(1)}K`
	}
	// Below a cent, hand off to the significant-digit notation rather than
	// rounding to `$0.00`. A holding worth two thirds of a cent is small; it is
	// not nothing, and this is the one renderer that used to say it was.
	if (a > 0 && a < 0.01) {
		return formatUsdPrice(n)
	}
	return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`
}

/// Thousands separators for a `parseAmount`-shaped decimal string, leaving the
/// fraction (and any in-progress trailing `.`) untouched. Anything else is
/// passed straight through — this never invents digits.
export function groupDigits(value: string): string {
	if (!/^\d*\.?\d*$/.test(value)) {
		return value
	}
	const dot = value.indexOf('.')
	const whole = dot === -1 ? value : value.slice(0, dot)
	if (whole.length < 4) {
		return value
	}
	return `${BigInt(whole).toLocaleString('en-US')}${dot === -1 ? '' : value.slice(dot)}`
}

/// Compact token quantity for dense rows — the unit-less sibling of
/// `usdCompact`: 1.29B / 44.27M / 260.6K, plain grouping below a thousand.
/// Magnitude is the message in a holders column; eleven raw digits are not.
export function numCompact(n: number | null | undefined): string {
	if (n === null || n === undefined || !Number.isFinite(n)) {
		return '—'
	}
	const a = Math.abs(n)
	if (a >= 1e12) {
		return `${(n / 1e12).toFixed(2)}T`
	}
	if (a >= 1e9) {
		return `${(n / 1e9).toFixed(2)}B`
	}
	if (a >= 1e6) {
		return `${(n / 1e6).toFixed(2)}M`
	}
	if (a >= 1e3) {
		return `${(n / 1e3).toFixed(1)}K`
	}
	return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

/// A token quantity with DYNAMIC decimals: grouped with two decimals from 1
/// up (compact tiers above a million), but significant digits below 1 — the
/// tape once rendered a real 0.001 frBTC trade as "0 frBTC" because a fixed
/// two-decimal cut rounds every sub-cent-of-a-token amount to the false zero
/// this codebase spends most of its number code refusing to print.
export function formatQty(n: number | null | undefined): string {
	if (n === null || n === undefined || !Number.isFinite(n)) {
		return '—'
	}
	if (n === 0) {
		return '0'
	}
	const sign = n < 0 ? '-' : ''
	const a = Math.abs(n)
	if (a >= 1e6) {
		return `${sign}${numCompact(a)}`
	}
	if (a >= 1) {
		return `${sign}${a.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
	}
	// Sub-unit: keep the significant digits, subscript-zero past the point
	// where a leading-zero run stops being countable (same convention as
	// prices — 0.001 renders "0.001", 0.00000042 renders "0.0₆42").
	const { zeros, digits } = splitSmallNumber(a, SIG_DIGITS)
	return `${sign}${renderSubUnit(zeros, digits)}`
}

/// A holder's share of supply. `null` (supply unknown server-side) renders a
/// dash; a real-but-tiny share says `<0.01%` rather than rounding to a zero —
/// the smallest position on the board is small, not nothing.
export function formatSupplyPct(pct: number | null | undefined): string {
	if (pct === null || pct === undefined || !Number.isFinite(pct)) {
		return '—'
	}
	if (pct > 0 && pct < 0.01) {
		return '<0.01%'
	}
	return `${pct.toFixed(2)}%`
}

/// bc1p… addresses middle-truncated for table cells: enough head to keep the
/// script prefix readable, enough tail to eyeball-match against a wallet.
/// Anything short enough to show whole is shown whole — truncating a string
/// into something LONGER (or equal) would be pure decoration.
export function truncateMiddle(value: string, head: number = 8, tail: number = 6): string {
	if (value.length <= head + tail + 1) {
		return value
	}
	return `${value.slice(0, head)}…${value.slice(-tail)}`
}

/// Coarse elapsed time, for "prices from 4m ago". Always rounds DOWN to the
/// unit so the label can never claim data is fresher than it is.
export function formatAge(ms: number): string {
	const seconds = Math.max(0, Math.floor(ms / 1000))
	if (seconds < 60) {
		return `${seconds}s`
	}
	const minutes = Math.floor(seconds / 60)
	if (minutes < 60) {
		return `${minutes}m`
	}
	const hours = Math.floor(minutes / 60)
	if (hours < 24) {
		return `${hours}h`
	}
	return `${Math.floor(hours / 24)}d`
}
